import { media } from '@goalpost/content';
import { Link } from '../../components/Link.tsx';
import { MediaVideo } from '../../components/MediaVideo.tsx';
import { Hero } from '../../components/marketing/Hero.tsx';
import { MarketingFooter } from '../../components/marketing/MarketingFooter.tsx';
import { CrossLinks } from '../../components/marketing/CrossLinks.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';
import { AGENT_DEMO_VIDEO } from '../../marketing-shots.ts';
import '../../styles/marketing.css';

export function FeatureAI() {
  const t = useT();
  useDocumentTitle(t('features-ai.document-title'));
  const hasAgentDemo = media(`${AGENT_DEMO_VIDEO}-light-cc.mp4`) !== null;

  return (
    <div className="mkt-page">
      <Hero
        eyebrow={t('features-ai.hero.eyebrow')}
        title={t('features-ai.hero.title')}
        lede={t('features-ai.hero.lede')}
      >
        <Link to="/register" className="mkt-btn primary lg">
          {t('features-ai.hero.cta-try')}
        </Link>
        <Link to="/dashboard" className="mkt-btn ghost lg">
          {t('marketing.cta.login')}
        </Link>
      </Hero>

      {/* The agent demo clip — absent until the media manifest carries it, so
          the page never shows a heading over an empty figure. */}
      {hasAgentDemo && (
        <section className="mkt-section" data-gp-name="agent-demo" data-gp-observe-impression="true">
          <div className="mkt-container">
            <div className="mkt-section-head">
              <span className="mkt-eyebrow">{t('features-ai.demo.eyebrow')}</span>
              <h2 className="mkt-h2">{t('features-ai.demo.heading')}</h2>
              <p className="mkt-section-lede">{t('features-ai.demo.lede')}</p>
            </div>
            <div style={{ maxWidth: '960px', margin: '0 auto' }}>
              <MediaVideo
                base={AGENT_DEMO_VIDEO}
                label={t('features-ai.demo.video-label')}
              />
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="mkt-section sunk" data-gp-name="bot-to-spec">
        <div className="mkt-container">
          <div className="mkt-split">
            <div className="mkt-mock" aria-hidden="true">
              <div className="mkt-mock-head">
                <span className="mkt-mock-head-eyebrow">AI draft</span>
                <span className="mkt-mock-head-title">From chat to changeBatch</span>
              </div>
              <div className="mkt-mock-body">
                <div className="mkt-prompt">
                  “After the meeting, update the spec to require PDF export
                  for Pro users.”
                </div>
                <div className="mkt-prompt-arrow" aria-hidden="true">↓</div>
                <div className="mkt-ops">
                  <div className="mkt-op add">
                    <span className="mkt-op-tag">ADD</span>
                    <span>Pro tier exports reports to PDF.</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mkt-split-body">
              <span className="mkt-eyebrow">{t('features-ai.bot-to-spec.eyebrow')}</span>
              <h3>{t('features-ai.bot-to-spec.heading')}</h3>
              <p>
                {t('features-ai.bot-to-spec.body-1')}
              </p>
              <p>
                {t('features-ai.bot-to-spec.body-2')}
              </p>
              <p>
                <Link
                  to="/docs/ai-integration"
                  className="mkt-inline-link"
                  data-gp-name="learn-more"
                >
                  {t('features-ai.bot-to-spec.docs-link')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reading: spec as context for downstream AI work */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-ai.spec-to-bot.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-ai.spec-to-bot.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-ai.spec-to-bot.lede')}
            </p>
          </div>
          <div className="mkt-pillars">
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-ai.spec-to-bot.code.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-ai.spec-to-bot.code.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-ai.spec-to-bot.code.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-ai.spec-to-bot.copy.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-ai.spec-to-bot.copy.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-ai.spec-to-bot.copy.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-ai.spec-to-bot.qa.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-ai.spec-to-bot.qa.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-ai.spec-to-bot.qa.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Can / can't */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-ai.boundaries.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-ai.boundaries.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-ai.boundaries.lede')}
            </p>
          </div>
          <div className="mkt-caps">
            <div className="mkt-caps-col can">
              <h4>{t('features-ai.boundaries.can.heading')}</h4>
              <ul>
                <li>{t('features-ai.boundaries.can.1')}</li>
                <li>{t('features-ai.boundaries.can.2')}</li>
                <li>{t('features-ai.boundaries.can.3')}</li>
                <li>{t('features-ai.boundaries.can.4')}</li>
                <li>{t('features-ai.boundaries.can.5')}</li>
              </ul>
            </div>
            <div className="mkt-caps-col cannot">
              <h4>{t('features-ai.boundaries.cannot.heading')}</h4>
              <ul>
                <li>{t('features-ai.boundaries.cannot.1')}</li>
                <li>{t('features-ai.boundaries.cannot.2')}</li>
                <li>{t('features-ai.boundaries.cannot.3')}</li>
                <li>{t('features-ai.boundaries.cannot.4')}</li>
                <li>{t('features-ai.boundaries.cannot.5')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Governance */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-ai.governance.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-ai.governance.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-ai.governance.lede')}
            </p>
          </div>
          <div className="mkt-pillars">
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-ai.governance.auth.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-ai.governance.auth.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-ai.governance.auth.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-ai.governance.audit.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-ai.governance.audit.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-ai.governance.audit.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-ai.governance.validation.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-ai.governance.validation.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-ai.governance.validation.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Availability */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-ai.availability.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-ai.availability.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-ai.availability.lede')}
            </p>
          </div>
        </div>
      </section>

      {/* Docs reference */}
      <section className="mkt-section" data-gp-name="setup">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-ai.docs.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-ai.docs.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-ai.docs.body')}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="mkt-cta-row">
              <Link
                to="/docs/ai-integration"
                className="mkt-btn ghost lg"
                data-gp-name="setup-guide-cta"
                data-gp-observe-interact="true"
              >
                {t('features-ai.docs.cta')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <h2 className="mkt-h2">{t('marketing.cta.keep-reading')}</h2>
          </div>
          <CrossLinks
            links={[
              {
                to: '/features/documents',
                eyebrow: t('marketing.feature.documents.eyebrow'),
                title: t('marketing.feature.documents.title'),
                desc: t('features-ai.crosslink.documents.desc'),
              },
              {
                to: '/features/approvals',
                eyebrow: t('marketing.feature.approvals.eyebrow'),
                title: t('marketing.feature.approvals.title'),
                desc: t('features-ai.crosslink.approvals.desc'),
              },
            ]}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-cta-strip">
        <div className="mkt-container-narrow">
          <h2 className="mkt-h2">{t('features-ai.cta.heading')}</h2>
          <p className="mkt-section-lede">
            {t('features-ai.cta.lede')}
          </p>
          <div className="mkt-cta-row">
            <Link to="/register" className="mkt-btn primary lg">
              {t('marketing.cta.signup-free')}
            </Link>
            <Link to="/dashboard" className="mkt-btn ghost lg">
              {t('marketing.cta.login')}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
